/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as CompanyLogo from '@/Components/Global/CompanyLogo/template.vue'
import * as DataForm from '@/Components/Global/DataForm/template.vue'
import * as DataTable from '@/Components/Global/DataTable/template.vue'
import * as SubmitButton from '@/Components/Global/SubmitButton/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { Vuex } from '@/Constants/Vuex'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'
import { StoreHelper } from '@/Helpers/Utils/StoreHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @internal Const @name PopupUserForm                                                          **/
/**************************************************************************************************/
const PopupUserForm = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'PopupUserForm', mixins: [MixinBase, MixinComponent, MixinResponsive],

	components:
	{
		CompanyLogo: CompanyLogo.default,
		DataForm: DataForm.default,
		DataTable: DataTable.default,
		SubmitButton: SubmitButton.default
	},

	props:
	{
		cancelButtonText: { type: String, default: 'Cerrar' },
		dataFormAction: Number,
		display: { type: String, default: 'Popup' },
		preventDefaults: { type: Boolean, default: false },
		show: Boolean,
		showStoragesList: Boolean,
		submitButtonTextOnInsert: String,
		submitButtonTextOnRead: String,
		submitButtonTextOnUpdate: String
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			dataForm: undefined as any,
			inputButtonClicked: undefined as string | undefined,
			states:
			{
				isFetching: false,
				secondaryButtonAcceptText: 'Aceptar',
				secondaryButtonCancelText: 'Cancelar',
				showActionButtons: true,
				showSecondaryButtonAccept: false,
				showSecondaryButtonCancel: false,
				showUpdate: true
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	mounted() : void
	{
		this.DataForm.Set<DataForm.DataFormRef['states']>({ action: this.dataFormAction })
		this.InitPermissions()
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		DataForm() : DataForm.DataFormRef
		{
			return this.$refs.dataForm as DataForm.DataFormRef
		},

		DataTable() : DataTable.DataTableRef
		{
			return this.$refs.dataTable as DataTable.DataTableRef
		},

		GetDisplayClass() : { [key: string]: boolean }
		{
			return this.display === 'Popup' ? { ['popup-bg']: true } : { ['non-popup-bg']: true }
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		GetDataFormCurrentTab() : any
		{
			return this.DataForm.GetCurrentTab()
		},

		InitPermissions() : void
		{
			if (this.showStoragesList)
			{
				this.DataTable.SetPermission('ACTION_DELETE', true)
				this.DataTable.SetPermission('NEW_BUTTON', true)
			}
		},

		OnClose() : void
		{
			PrintHelper.warning('[PopupUserForm]:onClose event triggered')
			this.$emit('onClose')
		},

		OnDTButtonClick(key: string, row: any) : void
		{
			PrintHelper.warning('[PopupUserForm]:onDTButtonClick event triggered')
			this.$emit('onDTButtonClick', key, row)
		},

		OnDTNewButtonClick(key: string) : void
		{
			PrintHelper.warning('[PopupUserForm]:onDTNewButtonClick event triggered')
			this.inputButtonClicked = key
			this.$emit('onDTNewButtonClick')
		},

		OnInputButtonClick(key: string, id: string) : void
		{
			PrintHelper.warning('[PopupUserForm]:onInputButtonClick event triggered')
			if (!this.preventDefaults) this.DataForm.Set<DataForm.DataFormRef['states']>({ showPopupTable: true })
			this.inputButtonClicked = key
			this.$emit('onInputButtonClick', key, id)
		},

		OnOptionChange(key: string, id: string) : void
		{
			PrintHelper.warning('[PopupUserForm]:onOptionChange event triggered')
			this.$emit('onOptionChange', key, id)
		},

		OnPTSelect(currentRow: object) : void
		{
			PrintHelper.warning('[PopupUserForm]:onPTSelect event triggered')
			this.$emit('onPTSelect', this.inputButtonClicked, currentRow)
		},

		OnPUFCurrentSearchPaginationChanged(page: number, searchParams: any) : void
		{
			PrintHelper.warning('[PopupUserForm]:onPUFCurrentSearchPaginationChanged event triggered')
			this.$emit('onPUFCurrentSearchPaginationChanged', page, searchParams)
		},

		OnPUFPaginationChanged(page: number) : void
		{
			PrintHelper.warning('[PopupUserForm]:onPUFPaginationChanged event triggered')
			this.$emit('onPUFPaginationChanged', page)
		},

		OnPUFRefreshButtonClick() : void
		{
			PrintHelper.warning('[PopupUserForm]:onPUFRefreshButtonClick event triggered')
			this.$emit('onPUFRefreshButtonClick', this.inputButtonClicked)
		},

		OnPUFSearchButtonClicked(searchParams: any) : void
		{
			PrintHelper.warning('[PopupUserForm]:onPUFSearchButtonClicked event triggered')
			this.$emit('onPUFSearchButtonClicked', searchParams)
		},

		OnPUFSecondaryButtonClick(key: string) : void
		{
			PrintHelper.warning('[PopupUserForm]:onPUFSecondaryButtonClick event triggered')
			this.$emit('onPUFSecondaryButtonClick', key)
		},

		OnPUFSubmit() : void
		{
			PrintHelper.warning('[PopupUserForm]:onPUFSubmit event triggered')
			this.$emit('onPUFSubmit')
		},

		OnRowDoubleClick(clickedRow: object) : void
		{
			PrintHelper.warning('[PopupUserForm]:onRowDoubleClick():Event triggered')
			this.$emit('onRowDoubleClick', this.inputButtonClicked, clickedRow)
		}
	},

	/*************************************************/
	/** @Watch                                      **/
	/*************************************************/
	watch:
	{
		...StoreHelper.watchStoreProperty(Vuex.Modules.Global.Names.Permissions, 'InitPermissions'),
		'dataFormAction'() : void { this.DataForm.Set<DataForm.DataFormRef['states']>({ action: this.dataFormAction }) }
	}
})

/**************************************************************************************************/
/** @export Component @name PopupUserForm                                                        **/
/**************************************************************************************************/
export default PopupUserForm

/**************************************************************************************************/
/** @export Type @name PopupUserFormRef                                                          **/
/**************************************************************************************************/
export type PopupUserFormRef = InstanceType<typeof PopupUserForm>